// CoverArt — generated cover art for posts that have no real image.
//
// This is the fallback layer for editorial covers. When a post carries an
// unfurled OpenGraph image (post.cover_image_url) the feed shows that photo;
// otherwise we render one of these.
//
// Visual language: deep-space scene on the site's own palette — a navy base
// (the dark-theme --bg), a division-colored nebula glow, a deterministic
// starfield, green orbital rings echoing the brand orbit mark, and a celestial
// motif per post type (planet for projects; satellite orbit or constellation,
// alternating deterministically by post id, for general posts). Deterministic
// per post id so a card always looks the same and no two neighbours match.
//
// Why generated SVG rather than bundled images or an AI image service:
// - zero binary assets, no licensing, no external fetch (CSP-safe),
// - instant + deterministic, theme-safe (a dark scene reads well on both the
//   cream light chrome and the dark space chrome — like a photo).
// A future upgrade could swap this for curated art or on-demand AI covers; the
// call site (EditorialCover) only needs a component that fills its box.

import type { PostType } from "../api";

// Division → accent hue, taken from the site's design tokens (index.css).
// Used for the nebula glow and the type motif so covers stay on-brand.
const DIVISION_ACCENT: Record<string, string> = {
  NBS: "#2f83bf", // --nbs, brightened for a dark field
  NFS: "#22a1ab", // --nfs
  NDS: "#8a5fc4", // --nds
  SS: "#d64f8f", // --ss
};
const GREEN = "#3b9f7b"; // --green (brand accent, used for the orbit rings)
const SPACE_TOP = "#101a2e"; // deep navy (near dark-theme --surface)
const SPACE_BOTTOM = "#080b14"; // near dark-theme --bg #0b0d13

// Cheap deterministic hash (djb2).
function hashSeed(seed: string): number {
  let h = 5381;
  for (let i = 0; i < seed.length; i++) h = (h * 33) ^ seed.charCodeAt(i);
  return Math.abs(h);
}

// Seeded LCG so the starfield is deterministic per post (no Math.random).
function makeRng(seed: number): () => number {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

interface CoverArtProps {
  type: PostType | string;
  division: string;
  /** Stable per-post seed (use post.id) so the art is deterministic. */
  seed: string;
  className?: string;
}

/**
 * A decorative, deterministic deep-space SVG cover. Fills its container and is
 * aria-hidden — it carries no information the title/kicker don't already.
 */
export function CoverArt({ type, division, seed, className }: CoverArtProps) {
  const accent = DIVISION_ACCENT[division] ?? DIVISION_ACCENT.NBS;
  const h = hashSeed(seed);
  const rand = makeRng(h);
  // SVG gradient ids must be unique across every cover on the page — derive
  // from the full (sanitized) post id, not a truncated hash, or two cards can
  // collide and one renders with the other's gradients. Fall back to the hash
  // only if the seed sanitizes to nothing.
  const sanitized = seed.replace(/[^a-zA-Z0-9]/g, "");
  const uid = `cv${sanitized || h.toString(36)}`;

  // Nebula placement + a little orbit tilt, deterministic per post.
  const nebX = 70 + (h % 260);
  const nebY = 40 + ((h >> 4) % 140);
  const tilt = -25 + (h % 50);

  // Starfield — ~16 stars of varied size/brightness.
  const stars = Array.from({ length: 16 }, () => ({
    x: +(rand() * 400).toFixed(1),
    y: +(rand() * 220).toFixed(1),
    r: +(0.5 + rand() * 1.7).toFixed(2),
    o: +(0.3 + rand() * 0.6).toFixed(2),
  }));

  const norm = type === "project" ? "project" : "post";

  return (
    <svg
      className={className}
      viewBox="0 0 400 220"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-hidden="true"
      style={{ display: "block", width: "100%", height: "100%" }}
    >
      <defs>
        <linearGradient id={`${uid}-sky`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0%" stopColor={SPACE_TOP} />
          <stop offset="100%" stopColor={SPACE_BOTTOM} />
        </linearGradient>
        {/* Division-colored nebula glow, fading to transparent. */}
        <radialGradient
          id={`${uid}-neb`}
          gradientUnits="userSpaceOnUse"
          cx={nebX}
          cy={nebY}
          r="180"
        >
          <stop offset="0%" stopColor={accent} stopOpacity="0.5" />
          <stop offset="55%" stopColor={accent} stopOpacity="0.14" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        {/* Soft green counter-glow for depth. */}
        <radialGradient
          id={`${uid}-neb2`}
          gradientUnits="userSpaceOnUse"
          cx={400 - nebX}
          cy={220 - nebY / 2}
          r="150"
        >
          <stop offset="0%" stopColor={GREEN} stopOpacity="0.22" />
          <stop offset="100%" stopColor={GREEN} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-planet`} cx="38%" cy="34%" r="72%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.35" />
          <stop offset="42%" stopColor={accent} stopOpacity="1" />
          <stop offset="100%" stopColor={SPACE_BOTTOM} stopOpacity="1" />
        </radialGradient>
      </defs>

      {/* Deep space */}
      <rect width="400" height="220" fill={`url(#${uid}-sky)`} />
      <rect width="400" height="220" fill={`url(#${uid}-neb)`} />
      <rect width="400" height="220" fill={`url(#${uid}-neb2)`} />

      {/* Starfield */}
      <g fill="#ffffff">
        {stars.map((s, i) => (
          <circle key={i} cx={s.x} cy={s.y} r={s.r} opacity={s.o} />
        ))}
      </g>

      {/* Faint orbit ring across the scene — echoes the brand orbit mark. */}
      <ellipse
        cx="200"
        cy="110"
        rx="150"
        ry="58"
        fill="none"
        stroke={GREEN}
        strokeOpacity="0.35"
        strokeWidth="1.5"
        transform={`rotate(${tilt} 200 110)`}
      />

      {/* Type motif, centered */}
      <g transform="translate(200 110)">
        {norm === "project" && <PlanetMotif uid={uid} accent={accent} tilt={tilt} />}
        {norm === "post" &&
          (h % 2 === 0 ? (
            <SatelliteMotif accent={accent} tilt={tilt} />
          ) : (
            <ConstellationMotif accent={accent} />
          ))}
      </g>
    </svg>
  );
}

// --- Motifs (drawn around a 0,0 origin) --------------------------------------

// Project → a planet you built, with a tilted ring.
function PlanetMotif({ uid, accent, tilt }: { uid: string; accent: string; tilt: number }) {
  return (
    <g>
      <ellipse
        cx="0"
        cy="0"
        rx="60"
        ry="20"
        fill="none"
        stroke={accent}
        strokeOpacity="0.85"
        strokeWidth="3"
        transform={`rotate(${tilt})`}
      />
      <circle cx="0" cy="0" r="34" fill={`url(#${uid}-planet)`} />
      <ellipse
        cx="0"
        cy="0"
        rx="60"
        ry="20"
        fill="none"
        stroke="#ffffff"
        strokeOpacity="0.5"
        strokeWidth="1.5"
        strokeDasharray="4 90"
        transform={`rotate(${tilt})`}
      />
    </g>
  );
}

// Link → a satellite orbiting a node: connection across a distance.
function SatelliteMotif({ accent, tilt }: { accent: string; tilt: number }) {
  return (
    <g transform={`rotate(${tilt})`}>
      <ellipse cx="0" cy="0" rx="64" ry="30" fill="none" stroke="#ffffff" strokeOpacity="0.55" strokeWidth="2" />
      <circle cx="0" cy="0" r="10" fill={accent} />
      <circle cx="0" cy="0" r="10" fill="none" stroke="#ffffff" strokeOpacity="0.7" strokeWidth="1.5" />
      {/* satellite on the orbit */}
      <g transform="translate(64 0)">
        <circle cx="0" cy="0" r="6" fill="#ffffff" />
        <circle cx="0" cy="0" r="11" fill="none" stroke="#ffffff" strokeOpacity="0.4" strokeWidth="1.5" />
      </g>
    </g>
  );
}

// Discussion → a constellation: stars connected into a dialogue.
function ConstellationMotif({ accent }: { accent: string }) {
  const pts = [
    [-54, -18],
    [-18, 14],
    [16, -22],
    [44, 10],
    [10, 30],
  ];
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]} ${p[1]}`).join(" ");
  return (
    <g>
      <path d={line} fill="none" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="1.5" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p[0]} cy={p[1]} r={i % 2 === 0 ? 5 : 3.4} fill="#ffffff" />
          <circle cx={p[0]} cy={p[1]} r={i % 2 === 0 ? 9 : 6} fill="none" stroke={accent} strokeOpacity="0.7" strokeWidth="1.5" />
        </g>
      ))}
    </g>
  );
}
